import type { Theme } from "../semantic/types";
import { baseTypography } from "./typography/base";
import { baseSpacing } from "./spacing/base";
import { baseRadii } from "./radii/base";
import { baseShadows } from "./shadows/base";
import { baseBorders } from "./borders/base";
import { baseLayout } from "./layout/base";
import { learnColors } from "./colors/learn";
import { spacing } from "../primitives/spacing";

export const learnTheme: Theme = {
  name: "learn",
  typography: baseTypography,
  spacing: baseSpacing,
  radii: baseRadii,
  shadows: baseShadows,
  borders: baseBorders,
  layout: baseLayout,
  colors: learnColors,

  text: {
    primary: learnColors.neutral[10],
    secondary: learnColors.neutral[40],
    tertiary: learnColors.neutral[60],
    disabled: learnColors.neutral[80],
    inverse: learnColors.neutral[100],
    interactive: learnColors.primary[50],
    placeholder: learnColors.neutral[50],
  },

  surface: {
    page: learnColors.neutral[95],
    card: learnColors.neutral[100],
    overlay: learnColors.overlay,
    sidebar: learnColors.primary[10],
    input: learnColors.neutral[100],
  },

  border: {
    default: learnColors.neutral[90],
    divider: learnColors.neutral[95],
    focus: learnColors.primary[50],
  },

  icon: {
    bottomPillActive: learnColors.primary[40],
    bottomPillInactive: learnColors.neutral[10],
  },

  input: {
    background: {
      default: learnColors.neutral[100],
      disabled: learnColors.neutral[95],
    },
    border: {
      default: learnColors.neutral[80],
      focus: learnColors.primary[50],
      error: learnColors.error[50],
    },
    padding: {
      sm: baseSpacing.sm,
      md: baseSpacing.md,
      lg: baseSpacing.lg,
    },
    radius: baseRadii.md,
  },

  select: {
    icon: {
      color: {
        default: learnColors.neutral[50],
        disabled: learnColors.neutral[80],
      },
    },
    item: {
      background: {
        default: learnColors.neutral[100],
        hover: learnColors.neutral[95],
        selected: learnColors.primary[10],
      },
      text: {
        default: learnColors.neutral[10],
        selected: learnColors.primary[60],
      },
    },
  },

  action: {
    primary: {
      default: learnColors.primary[50],
      hover: learnColors.primary[40],
      active: learnColors.primary[60],
      disabled: learnColors.primary[80],
      label: learnColors.neutral[100],
    },
    secondary: {
      default: learnColors.primary[95],
      hover: learnColors.primary[90],
      active: learnColors.primary[80],
      label: learnColors.primary[50],
    },
  },

  feedback: {
    info: learnColors.primary[50],
    success: learnColors.success[50],
    warning: learnColors.warning[50],
    error: learnColors.error[50],
  },

  control: {
    border: {
      default: learnColors.neutral[80],
      hover: learnColors.neutral[60],
      focus: learnColors.primary[50],
      active: learnColors.primary[60],
      disabled: learnColors.neutral[90],
      error: learnColors.error[50],
    },
    background: {
      default: learnColors.neutral[100],
      hover: learnColors.neutral[100],
      active: learnColors.neutral[95],
      disabled: learnColors.neutral[95],
    },
    text: {
      default: learnColors.neutral[10],
      placeholder: learnColors.neutral[50],
      disabled: learnColors.neutral[60],
      readonly: learnColors.neutral[40],
    },
    height: {
      sm: 32,
      md: 40,
      lg: 48,
    },
    padding: {
      x: {
        sm: baseSpacing.sm,
        md: baseSpacing.md,
        lg: baseSpacing.lg,
      },
    },
    radius: {
      sm: baseRadii.sm,
      md: baseRadii.md,
      lg: baseRadii.lg,
    },
  },
  textarea: {
    padding: {
      sm: baseSpacing.sm,
      md: spacing[5],
      lg: baseSpacing.lg,
    },
  },
};
